import React, { useEffect, useState } from 'react';
import { FiX } from 'react-icons/fi';
import { authAPI, UserProfile } from '../services/api';
import './ProfileModal.css';

interface ProfileModalProps {
  open: boolean;
  onClose: () => void;
  isStaff: boolean;
  onProfileUpdated?: () => void;
}

const ProfileModal: React.FC<ProfileModalProps> = ({
  open,
  onClose,
  isStaff,
  onProfileUpdated,
}) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newPasswordConfirm, setNewPasswordConfirm] = useState('');

  useEffect(() => {
    if (!open) return;

    let cancelled = false;
    setError(null);
    setSuccess(null);
    setLoading(true);
    setCurrentPassword('');
    setNewPassword('');
    setNewPasswordConfirm('');

    authAPI
      .getProfile()
      .then((p) => {
        if (cancelled) return;
        setProfile(p);
        setFirstName(p.first_name || '');
        setLastName(p.last_name || '');
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load profile');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open]);

  const handleSave = async () => {
    if (isStaff) return;
    setError(null);
    setSuccess(null);

    const pwTrim = newPassword.trim();
    const pwConfirmTrim = newPasswordConfirm.trim();
    const curPw = currentPassword;

    if (pwTrim || pwConfirmTrim) {
      if (!curPw) {
        setError('Enter your current password to set a new one.');
        return;
      }
      if (pwTrim.length < 8) {
        setError('New password must be at least 8 characters.');
        return;
      }
      if (pwTrim !== pwConfirmTrim) {
        setError('New password and confirmation do not match.');
        return;
      }
    }

    const payload: Parameters<typeof authAPI.patchProfile>[0] = {
      first_name: firstName,
      last_name: lastName,
    };

    if (pwTrim) {
      payload.current_password = curPw;
      payload.new_password = pwTrim;
      payload.new_password_confirm = pwConfirmTrim;
    }

    setSaving(true);
    try {
      const updated = await authAPI.patchProfile(payload);
      setProfile(updated);
      setFirstName(updated.first_name || '');
      setLastName(updated.last_name || '');
      setCurrentPassword('');
      setNewPassword('');
      setNewPasswordConfirm('');
      setSuccess('Profile updated.');
      onProfileUpdated?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save profile');
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  const displayEmail = profile?.email || '—';
  const displayUsername = profile?.username || '—';
  const displayOrg = profile?.organization || '—';

  return (
    <div
      className="profile-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="profile-modal-title"
      onClick={onClose}
    >
      <div className="profile-modal-panel" onClick={(e) => e.stopPropagation()}>
        <div className="profile-modal-header">
          <h2 id="profile-modal-title">Profile</h2>
          <button type="button" className="profile-modal-close" onClick={onClose} aria-label="Close">
            {React.createElement(FiX as React.ComponentType<{ size?: number }>, { size: 22 })}
          </button>
        </div>

        <div className="profile-modal-body">
          {loading && <div className="profile-modal-loading">Loading…</div>}

          {!loading && profile && (
            <>
              {isStaff && (
                <div className="profile-modal-banner">
                  Admin accounts are view-only here. To change this account, use Django admin.
                </div>
              )}

              {error && <div className="profile-modal-error">{error}</div>}
              {success && <div className="profile-modal-success">{success}</div>}

              <div className="profile-modal-field">
                <label htmlFor="profile-email">Email</label>
                <input id="profile-email" type="text" value={displayEmail} readOnly className="profile-modal-readonly" />
              </div>
              <div className="profile-modal-field">
                <label htmlFor="profile-username">Username</label>
                <input id="profile-username" type="text" value={displayUsername} readOnly className="profile-modal-readonly" />
              </div>
              <div className="profile-modal-field">
                <label htmlFor="profile-org">Organization</label>
                <input id="profile-org" type="text" value={displayOrg} readOnly className="profile-modal-readonly" />
              </div>

              <div className="profile-modal-field">
                <label htmlFor="profile-first">First name</label>
                <input
                  id="profile-first"
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  disabled={isStaff}
                  autoComplete="given-name"
                />
              </div>
              <div className="profile-modal-field">
                <label htmlFor="profile-last">Last name</label>
                <input
                  id="profile-last"
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  disabled={isStaff}
                  autoComplete="family-name"
                />
              </div>

              {!isStaff && (
                <>
                  <div className="profile-modal-section-title">Change password</div>
                  <div className="profile-modal-field">
                    <label htmlFor="profile-cur-pw">Current password</label>
                    <input
                      id="profile-cur-pw"
                      type="password"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      autoComplete="current-password"
                    />
                  </div>
                  <div className="profile-modal-field">
                    <label htmlFor="profile-new-pw">New password</label>
                    <input
                      id="profile-new-pw"
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      autoComplete="new-password"
                    />
                  </div>
                  <div className="profile-modal-field">
                    <label htmlFor="profile-new-pw2">Confirm new password</label>
                    <input
                      id="profile-new-pw2"
                      type="password"
                      value={newPasswordConfirm}
                      onChange={(e) => setNewPasswordConfirm(e.target.value)}
                      autoComplete="new-password"
                    />
                  </div>

                  <div className="profile-modal-actions">
                    <button type="button" className="profile-modal-btn profile-modal-btn-secondary" onClick={onClose}>
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="profile-modal-btn profile-modal-btn-primary"
                      onClick={handleSave}
                      disabled={saving}
                    >
                      {saving ? 'Saving…' : 'Save changes'}
                    </button>
                  </div>
                </>
              )}

              {isStaff && (
                <div className="profile-modal-actions">
                  <button type="button" className="profile-modal-btn profile-modal-btn-primary" onClick={onClose}>
                    Close
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProfileModal;
